import { useMemo } from 'react';
import { getPropertyValue } from './matrixClassification';

const DEFAULT_BAND_RANGE = { min: 0, max: 100, hasData: false };

/**
 * Compute the observed min/max of a threshold property for each band row,
 * by first assigning each feature to its nearest band (the same
 * nearest-neighbor logic that classifyWithMatrixFilter uses at runtime).
 *
 * This lets getMatrixFilterCoverageGaps compare per-band ceilings to
 * per-band observed maxima, avoiding false positives where a high value
 * in one band inflates the ceiling for another band.
 *
 * @param {Array} hexagons - Array of OpenLayers hexagon/DSP features
 * @param {string|null} bandPropertyId - Property ID used for band assignment (e.g. 'mean_height')
 * @param {string|null} thresholdPropertyId - Property ID measured per band (e.g. 'density')
 * @param {Array<number>} bands - Ordered array of band values from filter.bands
 * @returns {Array<{min: number, max: number, hasData: boolean}>} One entry per band index
 */
export default function usePerBandPropertyRanges(
	hexagons,
	bandPropertyId,
	thresholdPropertyId,
	bands
) {
	return useMemo(() => {
		if (!hexagons?.length || !bandPropertyId || !thresholdPropertyId || !bands?.length) {
			return bands?.map(() => DEFAULT_BAND_RANGE) || [];
		}

		// Initialize accumulators per band
		const accumulators = bands.map(() => ({ min: Infinity, max: -Infinity, count: 0 }));

		for (const h of hexagons) {
			const meta = h.get('data')?.metadata;
			if (!meta) continue;

			const bandValue = getPropertyValue(meta, bandPropertyId);
			const thresholdValue = getPropertyValue(meta, thresholdPropertyId);
			if (bandValue === null || isNaN(bandValue)) continue;
			if (thresholdValue === null || isNaN(thresholdValue)) continue;

			// Nearest-neighbor band assignment (mirrors classifyWithMatrixFilter)
			let bandIndex = 0;
			let minDiff = Math.abs(bandValue - bands[0]);
			for (let i = 1; i < bands.length; i++) {
				const diff = Math.abs(bandValue - bands[i]);
				if (diff < minDiff) {
					minDiff = diff;
					bandIndex = i;
				}
			}

			const acc = accumulators[bandIndex];
			acc.count++;
			if (thresholdValue < acc.min) acc.min = thresholdValue;
			if (thresholdValue > acc.max) acc.max = thresholdValue;
		}

		return accumulators.map(acc => {
			if (acc.min === Infinity) return DEFAULT_BAND_RANGE;
			return {
				min: acc.min,
				max: acc.max,
				hasData: true,
			};
		});
	}, [hexagons, bandPropertyId, thresholdPropertyId, bands]);
}
